import {
  DESK_COLUMNS,
  findDeskCategoryPreset,
  findDeskCategoryPresetByFeedCategory,
  usesDefaultDeskFeedCategory,
} from './deskConfig';

/** localStorage key for News-Agent Phase 0 prefs (collapse now; pins later). */
export const DESK_AGENT_PREFS_KEY = 'darknews.deskAgents.v1';
/** Bump to reset Auto-Search LED to off without wiping collapse prefs (Sprint 52 perf). */
export const AUTO_SEARCH_PREF_REV = 2;

export function defaultCollapsedMap() {
  return Object.fromEntries(DESK_COLUMNS.map((column) => [column.id, false]));
}

export function defaultAutoSearchMap() {
  return Object.fromEntries(DESK_COLUMNS.map((column) => [column.id, false]));
}

/** Per-column fetch-in-flight for progressive desk paint (PERF-QT-02). */
export function defaultColumnFetchPendingMap(pending = false) {
  return Object.fromEntries(DESK_COLUMNS.map((column) => [column.id, pending]));
}

/** Per-slot category override; null means use DESK_COLUMNS default. */
export function defaultCategoryOverrideMap() {
  return Object.fromEntries(DESK_COLUMNS.map((column) => [column.id, null]));
}

/**
 * Normalize a prefs blob into a full collapsed map for all known columns.
 * Unknown keys ignored; missing columns default to expanded (false).
 */
export function normalizeCollapsedMap(raw) {
  const next = defaultCollapsedMap();
  if (!raw || typeof raw !== 'object') {
    return next;
  }
  for (const column of DESK_COLUMNS) {
    const entry = raw[column.id];
    if (typeof entry === 'boolean') {
      next[column.id] = entry;
    } else if (entry && typeof entry === 'object' && typeof entry.collapsed === 'boolean') {
      next[column.id] = entry.collapsed;
    }
  }
  return next;
}

export function normalizeAutoSearchMap(raw) {
  const next = defaultAutoSearchMap();
  if (!raw || typeof raw !== 'object') {
    return next;
  }
  for (const column of DESK_COLUMNS) {
    const entry = raw[column.id];
    if (entry && typeof entry === 'object' && typeof entry.autoSearch === 'boolean') {
      next[column.id] = entry.autoSearch;
    }
  }
  return next;
}

function normalizeCategoryOverrideEntry(column, raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const feedCategory = typeof raw.feedCategory === 'string' ? raw.feedCategory.trim() : '';
  if (!feedCategory || usesDefaultDeskFeedCategory(column, { feedCategory })) {
    return null;
  }
  const preset = raw.presetId
    ? findDeskCategoryPreset(raw.presetId)
    : findDeskCategoryPresetByFeedCategory(feedCategory);
  if (preset && preset.feedCategory !== feedCategory) {
    return null;
  }
  if (!preset && !/^[a-z0-9_,.-]+$/i.test(feedCategory)) {
    return null;
  }
  return {
    feedCategory,
    label: typeof raw.label === 'string' && raw.label.trim()
      ? raw.label.trim()
      : (preset?.label || feedCategory),
    presetId: preset?.id || (typeof raw.presetId === 'string' ? raw.presetId : null),
  };
}

export function normalizeCategoryOverrideMap(raw) {
  const next = defaultCategoryOverrideMap();
  if (!raw || typeof raw !== 'object') {
    return next;
  }
  for (const column of DESK_COLUMNS) {
    const entry = raw[column.id];
    if (entry && typeof entry === 'object' && entry.categoryOverride) {
      next[column.id] = normalizeCategoryOverrideEntry(column, entry.categoryOverride);
    } else if (entry && typeof entry === 'object' && entry.feedCategory) {
      next[column.id] = normalizeCategoryOverrideEntry(column, entry);
    }
  }
  return next;
}

function defaultStorage() {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

function readPrefsBlob(storage = defaultStorage()) {
  try {
    if (!storage) {
      return {};
    }
    const raw = storage.getItem(DESK_AGENT_PREFS_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function readRawAgents(storage = defaultStorage()) {
  const parsed = readPrefsBlob(storage);
  return parsed?.deskAgents && typeof parsed.deskAgents === 'object' ? parsed.deskAgents : parsed;
}

function persistDeskAgents(deskAgents, storage, extra = {}) {
  const prev = readPrefsBlob(storage);
  storage.setItem(
    DESK_AGENT_PREFS_KEY,
    JSON.stringify({
      ...prev,
      deskAgents,
      ...extra,
    }),
  );
}

export function readCollapsedMap(storage = defaultStorage()) {
  return normalizeCollapsedMap(readRawAgents(storage));
}

export function readAutoSearchMap(storage = defaultStorage()) {
  const blob = readPrefsBlob(storage);
  const rev = typeof blob.autoSearchRev === 'number' ? blob.autoSearchRev : 0;
  if (rev < AUTO_SEARCH_PREF_REV) {
    return defaultAutoSearchMap();
  }
  const agents = blob?.deskAgents && typeof blob.deskAgents === 'object' ? blob.deskAgents : blob;
  return normalizeAutoSearchMap(agents);
}

export function readCategoryOverrideMap(storage = defaultStorage()) {
  return normalizeCategoryOverrideMap(readRawAgents(storage));
}

/**
 * Persist collapsed map. Shape keeps room for DA-13 bookmarks:
 * `{ deskAgents: { markets: { collapsed: true, autoSearch: false } } }`
 */
export function writeCollapsedMap(collapsedMap, storage = defaultStorage()) {
  if (!storage) {
    return;
  }
  const prevAgents = readRawAgents(storage);

  const deskAgents = { ...prevAgents };
  for (const column of DESK_COLUMNS) {
    const existing =
      prevAgents[column.id] && typeof prevAgents[column.id] === 'object'
        ? { ...prevAgents[column.id] }
        : {};
    deskAgents[column.id] = {
      ...existing,
      collapsed: Boolean(collapsedMap?.[column.id]),
      autoSearch: typeof existing.autoSearch === 'boolean' ? existing.autoSearch : false,
    };
  }
  persistDeskAgents(deskAgents, storage);
}

export function writeAutoSearchMap(autoSearchMap, storage = defaultStorage()) {
  if (!storage) {
    return;
  }
  const prevAgents = readRawAgents(storage);

  const deskAgents = { ...prevAgents };
  for (const column of DESK_COLUMNS) {
    const existing =
      prevAgents[column.id] && typeof prevAgents[column.id] === 'object'
        ? { ...prevAgents[column.id] }
        : {};
    deskAgents[column.id] = {
      ...existing,
      autoSearch: Boolean(autoSearchMap?.[column.id]),
      collapsed: typeof existing.collapsed === 'boolean' ? existing.collapsed : false,
    };
  }
  persistDeskAgents(deskAgents, storage, { autoSearchRev: AUTO_SEARCH_PREF_REV });
}

export function writeCategoryOverrideMap(overrideMap, storage = defaultStorage()) {
  if (!storage) {
    return;
  }
  const prevAgents = readRawAgents(storage);
  const deskAgents = { ...prevAgents };
  for (const column of DESK_COLUMNS) {
    const existing =
      prevAgents[column.id] && typeof prevAgents[column.id] === 'object'
        ? { ...prevAgents[column.id] }
        : {};
    const override = normalizeCategoryOverrideEntry(column, overrideMap?.[column.id]);
    if (override) {
      deskAgents[column.id] = {
        ...existing,
        categoryOverride: override,
      };
    } else {
      const next = { ...existing };
      delete next.categoryOverride;
      deskAgents[column.id] = next;
    }
  }
  persistDeskAgents(deskAgents, storage);
}

export function setColumnCollapsed(columnId, collapsed, storage = defaultStorage()) {
  const next = { ...readCollapsedMap(storage), [columnId]: Boolean(collapsed) };
  writeCollapsedMap(next, storage);
  return next;
}

export function setColumnAutoSearch(columnId, enabled, storage = defaultStorage()) {
  const next = { ...readAutoSearchMap(storage), [columnId]: Boolean(enabled) };
  writeAutoSearchMap(next, storage);
  return next;
}

export function setColumnCategoryOverride(columnId, override, storage = defaultStorage()) {
  const column = DESK_COLUMNS.find((entry) => entry.id === columnId);
  if (!column) {
    return readCategoryOverrideMap(storage);
  }
  const next = {
    ...readCategoryOverrideMap(storage),
    [columnId]: normalizeCategoryOverrideEntry(column, override),
  };
  writeCategoryOverrideMap(next, storage);
  return next;
}
